import React from 'react'
import { twMerge } from 'tailwind-merge'

import { HStack } from '../hstack'
import { Pressable } from '../pressable'
import { Text } from '../text'
import { gateChange } from './gateChange'
import { type SegmentedControlProps } from './SegmentedControl.types'

export function SegmentedControl({
    options,
    value,
    onChange,
    className,
    variant = 'pill',
}: SegmentedControlProps) {
    const isBoxed = variant === 'boxed'
    return (
        <HStack
            accessibilityRole='tablist'
            className={twMerge(
                // pill (default): Figma FilterSwitch — elevation track holding pills.
                // boxed: no track; segments carry their own border.
                isBoxed ? 'items-center' : 'rounded-2xl bg-background-elevation p-1',
                className,
            )}
        >
            {options.map((option, index) => {
                const isSelected = option.value === value
                const isFirst = index === 0
                const isLast = index === options.length - 1
                return (
                    <Pressable
                        key={option.value}
                        onPress={() => {
                            const next = gateChange(value, option.value)
                            if (next !== null) {
                                onChange(next)
                            }
                        }}
                        accessibilityRole='tab'
                        accessibilityState={{ selected: isSelected }}
                        className={twMerge(
                            'flex-1 items-center justify-center border-solid px-3',
                            isBoxed
                                ? twMerge(
                                      'min-h-[40px] bg-background-highlight',
                                      isSelected
                                          ? 'border-2 border-action-default'
                                          : 'border border-border-subtle',
                                      isFirst && 'rounded-l-[4px]',
                                      isLast && 'rounded-r-[4px]',
                                  )
                                : twMerge(
                                      'min-h-[28px] rounded-2xl border py-0.5',
                                      isSelected
                                          ? 'border-border-strong bg-background-surface'
                                          : 'border-transparent bg-transparent',
                                  ),
                        )}
                    >
                        <Text
                            size='body'
                            weight={isBoxed ? 'bold' : 'regular'}
                            tone={isBoxed || isSelected ? 'default' : 'muted'}
                            className='text-center'
                        >
                            {option.label}
                        </Text>
                    </Pressable>
                )
            })}
        </HStack>
    )
}
